(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-gg-payment-info]';

  function parseJSON(root, selector) {
    const node = root.querySelector(selector);

    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('[Gold Glow Payment Info] JSON inválido:', error);
      return null;
    }
  }

  function getFormatter(config) {
    return new Intl.NumberFormat(config.locale || 'pt-BR', {
      style: 'currency',
      currency: config.currency || 'BRL'
    });
  }

  function formatMoney(cents, formatter) {
    return formatter.format(cents / 100);
  }

  function getRate(config, installment) {
    if (installment <= Number(config.interestFreeInstallments)) {
      return 0;
    }

    return Number(config.rates?.[String(installment)] || 0);
  }

  function calculateInstallment(price, installment, config) {
    const hasInterest =
      installment > Number(config.interestFreeInstallments);

    const rate = getRate(config, installment);

    const total = hasInterest
      ? Math.round(price * ((100 + rate) / 100))
      : price;

    const value = Math.round(total / installment);

    return {
      installment,
      hasInterest,
      total,
      value,
      valid:
        installment === 1 ||
        value >= Number(config.minInstallment)
    };
  }

  function getPaymentState(price, config) {
    const freeLimit = Number(config.interestFreeInstallments);
    const maxLimit = Number(config.maxInstallments);

    let effectiveInterestFree = 1;
    let effectiveMax = 1;

    const rows = [];

    for (
      let installment = 1;
      installment <= maxLimit;
      installment += 1
    ) {
      const calculation = calculateInstallment(
        price,
        installment,
        config
      );

      if (!calculation.valid) continue;

      rows.push(calculation);

      effectiveMax = installment;

      if (installment <= freeLimit) {
        effectiveInterestFree = installment;
      }
    }

    const freeCalculation = calculateInstallment(
      price,
      effectiveInterestFree,
      config
    );

    const pixPrice = Math.round(
      price * ((100 - Number(config.pixDiscount)) / 100)
    );

    return {
      pixPrice,
      pixSavings: price - pixPrice,
      effectiveInterestFree,
      effectiveMax,
      freeInstallmentValue: freeCalculation.value,
      rows
    };
  }

  function findSelectedVariant(root, variants) {
    const variantMap = new Map(
      variants.map((variant) => [
        String(variant.id),
        variant
      ])
    );

    const section =
      root.closest('[id^="shopify-section-"]') ||
      root.closest('.shopify-section') ||
      document;

    const productInputs = Array.from(
      section.querySelectorAll(
        'form[action*="/cart/add"] input[name="id"]'
      )
    );

    for (const input of productInputs) {
      const formId = input.form?.id || '';

      /*
       * Ignora o form nativo de payment_terms do Shopify,
       * pois ele também possui um input name="id".
       */
      if (/installment/i.test(formId)) continue;

      const variant = variantMap.get(String(input.value));

      if (variant) {
        return variant;
      }
    }

    /*
     * Fallback: algumas implementações do tema
     * atualizam ?variant=ID na URL.
     */
    const urlVariant = new URL(window.location.href)
      .searchParams
      .get('variant');

    if (
      urlVariant &&
      variantMap.has(String(urlVariant))
    ) {
      return variantMap.get(String(urlVariant));
    }

    /*
     * Último fallback:
     * primeira variante recebida pelo Liquid.
     */
    return variants[0] || null;
  }

  function renderPayment(root, variant, config) {
    if (!variant) return;

    const price = Number(variant.price);

    if (!Number.isFinite(price)) return;

    const formatter = getFormatter(config);

    const state = getPaymentState(
      price,
      config
    );

    /*
     * PIX — resumo da página
     */
    root
      .querySelectorAll('[data-gg-pix-price]')
      .forEach((node) => {
        node.textContent = formatMoney(
          state.pixPrice,
          formatter
        );
      });

    /*
     * PIX — modal
     */
    root
      .querySelectorAll('[data-gg-modal-pix-price]')
      .forEach((node) => {
        node.textContent = formatMoney(
          state.pixPrice,
          formatter
        );
      });

    /*
     * Economia no PIX
     */
    root
      .querySelectorAll('[data-gg-pix-savings]')
      .forEach((node) => {
        node.textContent = formatMoney(
          state.pixSavings,
          formatter
        );
      });

    /*
     * Quantidade máxima SEM JUROS.
     * Também respeita parcela mínima de R$ 20.
     */
    root
      .querySelectorAll('[data-gg-summary-installments]')
      .forEach((node) => {
        node.textContent =
          state.effectiveInterestFree;
      });

    /*
     * Valor da melhor parcela sem juros.
     */
    root
      .querySelectorAll('[data-gg-installment-price]')
      .forEach((node) => {
        node.textContent = formatMoney(
          state.freeInstallmentValue,
          formatter
        );
      });

    /*
     * Máximo total de parcelas disponível.
     */
    const maxWrap = root.querySelector(
      '[data-gg-max-installments-wrap]'
    );

    const maxNode = root.querySelector(
      '[data-gg-max-installments]'
    );

    if (maxNode) {
      maxNode.textContent =
        state.effectiveMax;
    }

    /*
     * Se não houver parcelamento além do número
     * de parcelas sem juros, esconde:
     * "· até Xx no cartão"
     */
    if (maxWrap) {
      maxWrap.hidden =
        state.effectiveMax <=
        state.effectiveInterestFree;
    }

    /*
     * Reconstrói tabela do modal.
     */
    const list = root.querySelector(
      '[data-gg-installment-list]'
    );

    if (list) {
      const rows = state.rows.map((row) => {
        const wrapper =
          document.createElement('div');

        const left =
          document.createElement('span');

        const right =
          document.createElement('span');

        wrapper.className =
          'gg-payment-modal__installment-row';

        wrapper.dataset.ggInstallment =
          String(row.installment);

        left.textContent =
          `${row.installment}x de ` +
          `${formatMoney(row.value, formatter)} ` +
          `${row.hasInterest ? 'com juros' : 'sem juros'}`;

        right.textContent =
          `Total: ${formatMoney(row.total, formatter)}`;

        wrapper.append(
          left,
          right
        );

        return wrapper;
      });

      list.replaceChildren(...rows);
    }

    root.dataset.ggCurrentVariantId =
      String(variant.id);
  }

  function bindModal(root) {
    const modal = root.querySelector(
      '[data-gg-payment-modal]'
    );

    const openButton = root.querySelector(
      '[data-gg-payment-open]'
    );

    const closeButton = root.querySelector(
      '[data-gg-payment-close]'
    );

    const tabs = root.querySelectorAll(
      '[data-gg-payment-tab]'
    );

    const panels = root.querySelectorAll(
      '[data-gg-payment-panel]'
    );

    if (!modal || !openButton) return;

    /*
     * Abrir modal.
     */
    openButton.addEventListener('click', () => {
      if (typeof modal.showModal === 'function') {
        modal.showModal();
      }
    });

    /*
     * Fechar no X.
     */
    closeButton?.addEventListener('click', () => {
      modal.close();
    });

    /*
     * Fechar clicando fora do conteúdo.
     */
    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        modal.close();
      }
    });

    /*
     * Abas Cartão / PIX.
     */
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const target =
          tab.dataset.ggPaymentTab;

        tabs.forEach((item) => {
          item.classList.remove('is-active');
        });

        panels.forEach((panel) => {
          panel.classList.remove('is-active');
        });

        tab.classList.add('is-active');

        root
          .querySelector(
            `[data-gg-payment-panel="${target}"]`
          )
          ?.classList.add('is-active');
      });
    });
  }

  function initPaymentInfo(root) {
    if (
      !(root instanceof Element) ||
      root.dataset.ggInitialized === 'true'
    ) {
      return;
    }

    const config = parseJSON(
      root,
      '[data-gg-config]'
    );

    const variants = parseJSON(
      root,
      '[data-gg-variants]'
    );

    if (
      !config ||
      !Array.isArray(variants) ||
      variants.length === 0
    ) {
      return;
    }

    root.dataset.ggInitialized =
      'true';

    bindModal(root);

    const section =
      root.closest('[id^="shopify-section-"]') ||
      root.closest('.shopify-section') ||
      document;

    let timerIds = [];

    /*
     * Busca a variante atualmente selecionada
     * e recalcula todo o widget.
     */
    const sync = () => {
      const variant =
        findSelectedVariant(
          root,
          variants
        );

      if (!variant) return;

      renderPayment(
        root,
        variant,
        config
      );
    };

    /*
     * O Ceramide pode atualizar o input da variante
     * alguns milissegundos depois do clique.
     *
     * Por isso verificamos novamente em pequenos
     * intervalos após a interação.
     */
    const scheduleSync = () => {
      timerIds.forEach((timerId) => {
        window.clearTimeout(timerId);
      });

      timerIds = [
        0,
        50,
        150,
        300,
        600
      ].map(
        (delay) =>
          window.setTimeout(
            sync,
            delay
          )
      );
    };

    /*
     * Mudanças em selects/radios/variantes.
     */
    section.addEventListener(
      'change',
      scheduleSync,
      true
    );

    /*
     * Alguns seletores de variante do tema
     * são buttons, por isso também ouvimos click.
     */
    section.addEventListener(
      'click',
      scheduleSync,
      true
    );

    /*
     * Eventos comuns utilizados por temas Shopify.
     */
    [
      'variant:change',
      'variant-change',
      'product:variant-change',
      'product:variant:change'
    ].forEach((eventName) => {
      section.addEventListener(
        eventName,
        scheduleSync,
        true
      );
    });

    /*
     * Outra redundância útil:
     * quando o Ceramide atualiza o preço nativo,
     * verificamos novamente a variante.
     */
    const priceBlock =
      root.parentElement?.querySelector(
        '[id^="price-"]'
      );

    if (priceBlock) {
      const priceObserver =
        new MutationObserver(
          scheduleSync
        );

      priceObserver.observe(
        priceBlock,
        {
          childList: true,
          subtree: true,
          characterData: true
        }
      );
    }

    /*
     * Caso a variante seja refletida na URL.
     */
    window.addEventListener(
      'popstate',
      scheduleSync
    );

    /*
     * Primeira renderização.
     */
    sync();
  }

  function initWithin(container = document) {
    if (
      container instanceof Element &&
      container.matches(ROOT_SELECTOR)
    ) {
      initPaymentInfo(container);
    }

    container
      .querySelectorAll?.(
        ROOT_SELECTOR
      )
      .forEach(
        initPaymentInfo
      );
  }

  /*
   * Inicialização normal.
   */
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initWithin(document),
      { once: true }
    );
  } else {
    initWithin(document);
  }

  /*
   * Theme Editor da Shopify.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initWithin(event.target);
    }
  );

  /*
   * Suporte a conteúdo carregado dinamicamente.
   */
  const pageObserver =
    new MutationObserver(
      (mutations) => {
        mutations.forEach(
          (mutation) => {
            mutation.addedNodes.forEach(
              (node) => {
                if (
                  node instanceof Element
                ) {
                  initWithin(node);
                }
              }
            );
          }
        );
      }
    );

  pageObserver.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );
})();